/**
 * Normalizacion y construccion de documentos.
 *
 * `normalizeRichContent` es la funcion critica de compatibilidad: acepta
 * cualquiera de las formas historicas que pueden existir en
 * `blog_posts.content` y devuelve siempre un `RichDoc` valido.
 *
 * Formas soportadas (de mas nueva a mas vieja):
 *   1. `{ version: 2, doc: <RichDoc>, body: [...] }`  — formato actual
 *   2. `{ doc: <RichDoc> }`                            — doc sin version
 *   3. `<RichDoc>`                                     — doc plano
 *   4. `{ html: "<p>..</p>" }`                          — alguien guardo HTML
 *   5. `{ body: ["parrafo", ...] }`                    — formato legacy
 *   6. `"texto plano"` o `["parrafo", ...]`            — texto plano crudo
 *
 * Ninguna publicacion existente se modifica: la conversion ocurre al leer y
 * el doc nuevo solo se persiste cuando el usuario guarda desde el editor.
 */

import {
  MARK_TYPES,
  RICH_CONTENT_VERSION,
  type FaqEntry,
  type RichContent,
  type RichDoc,
  type RichNode,
} from "./types";
import { cleanPastedPlainText, collapseWhitespace, normalizeNewlines, splitParagraphs } from "./text";

export const EMPTY_DOC: RichDoc = { type: "doc", content: [{ type: "paragraph" }] };

export function text(value: string, marks?: RichNode["marks"]): RichNode {
  const node: RichNode = { type: "text", text: value };
  if (marks && marks.length > 0) node.marks = marks;
  return node;
}

export function paragraph(...content: RichNode[]): RichNode {
  return content.length > 0 ? { type: "paragraph", content } : { type: "paragraph" };
}

export function heading(level: 2 | 3, value: string): RichNode {
  return { type: "heading", attrs: { level }, content: [text(value)] };
}

export function bulletList(items: string[]): RichNode {
  return {
    type: "bulletList",
    content: items.map((item) => ({ type: "listItem", content: [paragraph(text(item))] })),
  };
}

export function orderedList(items: string[]): RichNode {
  return {
    type: "orderedList",
    content: items.map((item) => ({ type: "listItem", content: [paragraph(text(item))] })),
  };
}

export function blockquote(value: string): RichNode {
  return { type: "blockquote", content: [paragraph(text(value))] };
}

export function horizontalRule(): RichNode {
  return { type: "horizontalRule" };
}

export function image(attrs: {
  src: string;
  alt?: string | null;
  title?: string | null;
  caption?: string | null;
}): RichNode {
  const node: RichNode = { type: "image", attrs: { src: attrs.src } };
  if (attrs.alt) node.attrs = { ...node.attrs, alt: attrs.alt };
  if (attrs.title) node.attrs = { ...node.attrs, title: attrs.title };
  if (attrs.caption) node.attrs = { ...node.attrs, caption: attrs.caption };
  return node;
}

export function faqList(items: { question: string; answer: string }[]): RichNode {
  return {
    type: "faqList",
    content: items.map((item) => ({
      type: "faqItem",
      attrs: { question: item.question, answer: item.answer },
    })),
  };
}

export function doc(...content: RichNode[]): RichDoc {
  return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRichDoc(value: unknown): value is RichDoc {
  if (!isRecord(value)) return false;
  if (value.type !== "doc") return false;
  return Array.isArray(value.content);
}

/** Descarta nodos no soportados en cualquier posicion del arbol. */
function sanitizeNode(node: unknown): RichNode | null {
  if (!isRecord(node)) return null;
  if (typeof node.type !== "string") return null;

  if (node.type === "text") {
    if (typeof node.text !== "string") return null;
    const marks = Array.isArray(node.marks) ? node.marks.filter(isSafeMark) : [];
    return marks.length > 0 ? { type: "text", text: node.text, marks } : { type: "text", text: node.text };
  }

  if (node.type === "hardBreak" || node.type === "horizontalRule") return { type: node.type };

  const attrs = isRecord(node.attrs) ? { ...node.attrs } : undefined;

  const content = Array.isArray(node.content)
    ? node.content.map(sanitizeNode).filter((child): child is RichNode => child !== null)
    : undefined;

  const safe: RichNode = { type: node.type };
  if (attrs && Object.keys(attrs).length > 0) safe.attrs = attrs;
  if (content && content.length > 0) safe.content = content;
  return safe;
}

function isSafeMark(mark: unknown): boolean {
  return (
    isRecord(mark) && typeof mark.type === "string" && (MARK_TYPES as readonly string[]).includes(mark.type)
  );
}

/** Sanitiza un doc completo y garantiza al menos un bloque vacio editable. */
export function sanitizeDoc(input: RichDoc): RichDoc {
  const content = input.content.map(sanitizeNode).filter((node): node is RichNode => node !== null);
  return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] };
}

/**
 * Convierte HTML plano en nodos de bloque.
 *
 * Es un parser deliberadamente minimo y *estructural*: solo reconoce
 * `<h2> <h3> <p> <ul> <ol> <li> <blockquote> <hr> <img>` y tira las etiquetas
 * unknown. No se usa para sanear HTML del usuario (el admin no acepta HTML
 * crudo), solo para rescatar publicaciones donde alguien guardo HTML a mano.
 */
export function docFromHtml(html: string): RichDoc {
  const withoutComments = html.replace(/<!--[\s\S]*?-->/g, "");
  const blocks: RichNode[] = [];
  const pattern = /<(h1|h2|h3|p|ul|ol|blockquote|hr|img)\b([^>]*)>([\s\S]*?)<\/\1>|<(hr)\b[^>]*\/?>/gi;

  let match: RegExpExecArray | null;
  while ((match = pattern.exec(withoutComments)) !== null) {
    const tag = (match[1] ?? match[4] ?? "").toLowerCase();
    const attrs = match[2] ?? "";
    const inner = match[3] ?? "";

    if (tag === "hr") {
      blocks.push(horizontalRule());
      continue;
    }

    if (tag === "img") {
      const src = readAttribute(attrs, "src");
      if (src) {
        blocks.push(image({ src, alt: readAttribute(attrs, "alt"), title: readAttribute(attrs, "title") }));
      }
      continue;
    }

    if (tag === "h1" || tag === "h2" || tag === "h3") {
      const value = collapseWhitespace(stripTags(inner));
      if (!value) continue;
      // Un H1 pegado se degrada a H2: el H1 lo genera el titulo del post.
      blocks.push(heading(tag === "h3" ? 3 : 2, value));
      continue;
    }

    if (tag === "ul" || tag === "ol") {
      const items = [...inner.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)]
        .map((item) => collapseWhitespace(stripTags(item[1] ?? "")))
        .filter(Boolean);
      if (items.length > 0) blocks.push(tag === "ul" ? bulletList(items) : orderedList(items));
      continue;
    }

    if (tag === "blockquote") {
      const value = collapseWhitespace(stripTags(inner));
      if (value) blocks.push(blockquote(value));
      continue;
    }

    const value = collapseWhitespace(stripTags(inner));
    if (value) blocks.push(paragraph(text(value)));
  }

  return blocks.length > 0 ? { type: "doc", content: blocks } : EMPTY_DOC;
}

function readAttribute(attrs: string, name: string): string | null {
  const match = new RegExp(`${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(attrs);
  const value = match?.[2] ?? match?.[3] ?? match?.[4] ?? "";
  return value ? decodeBasicEntities(value) : null;
}

function stripTags(value: string): string {
  return value.replace(/<[^>]*>/g, " ");
}

function decodeBasicEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/**
 * Convierte texto plano en un doc de parrafos. Es la base de la accion
 * "Estructurar contenido", que despues-promueve algunos de esos parrafos.
 */
export function docFromPlainText(value: string): RichDoc {
  const cleaned = cleanPastedPlainText(value);
  const paragraphs = splitParagraphs(cleaned);
  return paragraphs.length > 0
    ? { type: "doc", content: paragraphs.map((item) => paragraph(text(item))) }
    : EMPTY_DOC;
}

/** Punto de entrada unico de compatibilidad. Ver docblock del archivo. */
export function normalizeRichContent(content: unknown): RichDoc {
  if (content === null || content === undefined) return EMPTY_DOC;

  if (typeof content === "string") {
    const trimmed = content.trim();
    if (!trimmed) return EMPTY_DOC;
    return trimmed.startsWith("<") ? docFromHtml(trimmed) : docFromPlainText(trimmed);
  }

  if (Array.isArray(content)) {
    return content.every((item) => typeof item === "string")
      ? docFromPlainText(normalizeNewlines(content.join("\n\n")))
      : EMPTY_DOC;
  }

  if (!isRecord(content)) return EMPTY_DOC;

  if (isRichDoc(content)) return sanitizeDoc(content);
  if (isRichDoc(content.doc)) return sanitizeDoc(content.doc);

  const html = content.html;
  if (typeof html === "string" && html.trim()) return docFromHtml(html);

  const body = content.body;
  if (Array.isArray(body)) {
    return docFromPlainText(body.filter((item): item is string => typeof item === "string").join("\n\n"));
  }
  if (typeof body === "string") return docFromPlainText(body);

  return EMPTY_DOC;
}

/** Indica si el contenido viene del formato estructurado nuevo. */
export function hasStructuredContent(content: unknown): boolean {
  if (!isRecord(content)) return false;
  if (isRichDoc(content.doc)) return true;
  if (isRichDoc(content)) return true;
  return typeof content.html === "string" && content.html.trim().length > 0;
}

/** Texto plano de un doc, nodo por nodo. */
export function docToPlainText(doc: RichDoc): string {
  const parts: string[] = [];

  const walk = (node: RichNode) => {
    if (node.type === "text") {
      if (node.text) parts.push(node.text);
      return;
    }
    if (node.type === "image") return;
    if (node.type === "faqItem") {
      const question = String(node.attrs?.question ?? "");
      const answer = String(node.attrs?.answer ?? "");
      if (question) parts.push(question);
      if (answer) parts.push(answer);
      return;
    }
    if (node.type === "horizontalRule" || node.type === "hardBreak") {
      parts.push("\n");
      return;
    }
    if (node.type === "listItem") {
      node.content?.forEach(walk);
      parts.push("\n");
      return;
    }
    node.content?.forEach(walk);
    if (["paragraph", "heading", "blockquote", "codeBlock", "tableRow"].includes(node.type)) parts.push("\n");
  };

  doc.content.forEach(walk);
  return normalizeNewlines(parts.join(""))
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Parrafos planos para la clave `body` de compatibilidad. */
export function docToLegacyBody(doc: RichDoc): string[] {
  return docToPlainText(doc)
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Separa el bloque FAQ del cuerpo.
 *
 * El FAQ es un node atomico: en el editor se edita con un formulario aparte, no
 * escribiendo dentro del documento. Al cargar una publicacion hay que quitarlo
 * del cuerpo para que no aparezca como una caja vacia en medio del texto; al
 * guardar hay que volver a ponerlo (ver `withFaqEntries`).
 */
export function splitFaq(doc: RichDoc): { doc: RichDoc; faq: FaqEntry[] } {
  const faq: FaqEntry[] = [];

  const collect = (node: RichNode) => {
    if (node.type === "faqList") {
      for (const item of node.content ?? []) {
        const question = String(item.attrs?.question ?? "").trim();
        const answer = String(item.attrs?.answer ?? "").trim();
        // Una pregunta sin respuesta no es una FAQ: emitirla en el schema
        // seria prometer algo que la pagina no muestra.
        if (question && answer) faq.push({ question, answer });
      }
    }
  };

  doc.content.forEach(collect);
  return { doc: { ...doc, content: doc.content.filter((node) => node.type !== "faqList") }, faq };
}

/** Reinserta el bloque FAQ al final del cuerpo. Es el inverso de `splitFaq`. */
export function withFaqEntries(doc: RichDoc, faq: FaqEntry[]): RichDoc {
  const { doc: body, faq: existing } = splitFaq(doc);
  const merged = [...existing];
  for (const entry of faq) {
    const question = entry.question.trim();
    const answer = entry.answer.trim();
    if (question && answer) merged.push({ question, answer });
  }
  if (merged.length === 0) return body;
  return {
    ...body,
    content: [...body.content, faqList(merged.map(({ question, answer }) => ({ question, answer })))],
  };
}

/**
 * Empaqueta el doc en el formato persistido, incluyendo `body` para que los
 * readers legacy sigan funcionando sin cambiar una sola linea.
 */
export function toRichContent(doc: RichDoc): RichContent {
  return {
    version: RICH_CONTENT_VERSION,
    doc: sanitizeDoc(doc),
    body: docToLegacyBody(doc),
  };
}
